import { Card, Form, FormButtonRow, FormGroup, FormParagraph, IconButton } from '@ksyos/design-system';
import { pick } from 'lodash';
import { useEffect, useState } from 'react';
import type { IPractitionerGeneralSettings } from '@frontend/components/interfaces/practitionerGeneralSettings';
import type { ValidationErrors } from '@frontend/utils/';
import { Gender } from '@common/enums/gender';
import { generalSettingsSchema } from '@common/validation/practitioner';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { InputRadioGroup } from '@frontend/components/userDataManagement/InputRadioGroup';
import { InputTextField } from '@frontend/components/userDataManagement/InputText';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { translateKey } from '@frontend/translate';
import { emptyValidationErrors, updateValidationErrorsForOne, useAbortableFetch, validate } from '@frontend/utils/';

export interface ICommonPersonalSettingProps {
    practitionerId: string;
}

export function GeneralSettings(props: ICommonPersonalSettingProps) {
    const [originalGeneralSettings, setOriginalGeneralSettings] = useState<IPractitionerGeneralSettings>();
    const [generalSettings, setGeneralSettings] = useState<IPractitionerGeneralSettings>();
    const [canEdit, setCanEdit] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        loadGeneralSettings();
    }, [props.practitionerId]);

    if (!generalSettings) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'generalSettings',
                item: generalSettings,
                errors: errors,
                onChange: async (key, value) => await handleChange(key, value),
                onDoValidate: async (key) => await validateOne(key),
            }}
        >
            <Card>
                <Card.Transition type={canEdit && isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !isEditing && canEdit ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translateKey('profile.generalSettings')}
                />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        {renderTextInput('nameGiven', 'common.nameGiven', generalSettings.nameGiven, false)}
                        {renderTextInput('nameInfix', 'common.nameInfix2', generalSettings.nameInfix, false)}
                        {renderTextInput('nameFamily', 'common.nameFamily', generalSettings.nameFamily)}

                        {(!isEditing || !generalSettings.authorization.canUpdateProperties?.includes('gender')) && (
                            <FormGroup label={translateKey('common.gender')}>
                                <FormParagraph>
                                    {generalSettings.gender
                                        ? translateKey(`genderEnum.${generalSettings.gender}`)
                                        : '-'}
                                </FormParagraph>
                            </FormGroup>
                        )}
                        {isEditing && generalSettings.authorization.canUpdateProperties?.includes('gender') && (
                            <InputRadioGroup
                                fieldKey="gender"
                                isRequired
                                label="common.gender"
                                radioItems={Object.keys(Gender).map((gender) => ({
                                    id: gender,
                                    label: translateKey(`genderEnum.${gender}`),
                                    value: gender,
                                }))}
                            />
                        )}

                        {renderTextInput(
                            'mobileNumber',
                            'common.mobileTelephoneNumber',
                            generalSettings.mobileNumber,
                            false
                        )}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => await handleCancel()}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={async () => await handleSubmit()}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    function renderTextInput(
        fieldKey: keyof IPractitionerGeneralSettings,
        label: string,
        value: string | null,
        required: boolean = true
    ) {
        const editMode = isEditing && generalSettings?.authorization.canUpdateProperties?.includes(fieldKey);
        if (editMode) {
            return (
                <InputTextField
                    dontValidateOnBlur
                    error={errors.get(fieldKey)}
                    fieldKey={fieldKey}
                    hasErrors={errors.has(fieldKey)}
                    isRequired={required}
                    label={label}
                    nullable
                />
            );
        } else {
            return (
                <FormGroup label={translateKey(label)}>
                    <FormParagraph>{value ? value : '-'}</FormParagraph>
                </FormGroup>
            );
        }
    }

    async function handleChange<K extends keyof IPractitionerGeneralSettings>(
        name: K,
        value: IPractitionerGeneralSettings[K]
    ) {
        setGeneralSettings({ ...generalSettings, [name]: value || null } as IPractitionerGeneralSettings);
    }

    async function validateOne<K extends keyof IPractitionerGeneralSettings>(name: K) {
        setErrors(updateValidationErrorsForOne(generalSettingsSchema, generalSettings, errors, name));
    }

    async function loadGeneralSettings() {
        const url = `/api/practitioners/${props.practitionerId}/generalSettings?includeItemAuthorizations=canUpdate,canUpdateProperties`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        setOriginalGeneralSettings(result.json);
        setGeneralSettings(result.json);
        setCanEdit(result.json.authorization?.canUpdate);
    }

    async function handleCancel() {
        setGeneralSettings(originalGeneralSettings);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    async function handleSubmit() {
        if (generalSettings) {
            setIsSaving(true);

            const filteredGeneralSettings = {
                authorization: { ...generalSettings.authorization },
                ...pick(generalSettings, generalSettings.authorization.canUpdateProperties!),
            };
            const { errors: validationErrors, item } = validate(generalSettingsSchema, filteredGeneralSettings);

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                const url = `/api/practitioners/${generalSettings.id}/generalSettings`;
                await abortableFetch.fetchJson(url, 'POST', item);
                setOriginalGeneralSettings(generalSettings);
                setErrors(emptyValidationErrors);
                setIsEditing(false);
            }

            setIsSaving(false);
        }
    }
}
